import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  MagnifyingGlassIcon,
  MapIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  InformationCircleIcon,
  XMarkIcon,
  MapPinIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  Bars3Icon,
  UserIcon,
  ChevronLeftIcon,
  PhotoIcon,
  LinkIcon,
  PaperClipIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import PublicObituaries from './PublicObituaries';
import PublicObituaryDetail from './PublicObituaryDetail';

const MAPBOX_DEFAULT_TOKEN = 'pk.eyJ1Ijoic2hhd25jcmFpbmUiLCJhIjoiY2xzN3Z5bmhwMjhzZTJrbnE5MGZ6NTI3YSJ9.K3ZOPNCbtyu6KYEtVO2jkQ';
mapboxgl.accessToken = (process.env.REACT_APP_MAPBOX_TOKEN && process.env.REACT_APP_MAPBOX_TOKEN.trim()) || MAPBOX_DEFAULT_TOKEN;

const PublicPortal = ({ showLoginLink = false, showAccountLink = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map state
  const [map, setMap] = useState(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Plot detail state
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [plotDetails, setPlotDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Image slider state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [sliderFullscreen, setSliderFullscreen] = useState(false);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  // UI state
  const [activeTab, setActiveTab] = useState('map');
  const [documents, setDocuments] = useState([]);
  const [products, setProducts] = useState([]);
  const [pricingShowTags, setPricingShowTags] = useState(false);
  const [pricingShowFilters, setPricingShowFilters] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Obituary state
  const [selectedObituarySlug, setSelectedObituarySlug] = useState(null);

  const popupRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Calculate approximate area of a polygon in square feet
  const calculatePlotArea = useCallback((geometry) => {
    if (!geometry || !geometry.coordinates) return null;

    const calculateRingArea = (ring) => {
      if (!ring || ring.length < 3) return 0;

      const midLat = ring.reduce((sum, coord) => sum + coord[1], 0) / ring.length;
      const latFeetPerDegree = 364000;
      const lngFeetPerDegree = 364000 * Math.cos(midLat * Math.PI / 180);

      const feetCoords = ring.map(coord => [
        coord[0] * lngFeetPerDegree,
        coord[1] * latFeetPerDegree
      ]);

      let area = 0;
      for (let i = 0; i < feetCoords.length - 1; i++) {
        area += feetCoords[i][0] * feetCoords[i + 1][1];
        area -= feetCoords[i + 1][0] * feetCoords[i][1];
      }
      return Math.abs(area / 2);
    };

    let totalArea = 0;
    if (geometry.type === 'Polygon') {
      totalArea = calculateRingArea(geometry.coordinates[0]);
      for (let i = 1; i < geometry.coordinates.length; i++) {
        totalArea -= calculateRingArea(geometry.coordinates[i]);
      }
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygon => {
        let polygonArea = calculateRingArea(polygon[0]);
        for (let i = 1; i < polygon.length; i++) {
          polygonArea -= calculateRingArea(polygon[i]);
        }
        totalArea += polygonArea;
      });
    }

    return totalArea > 0 ? totalArea.toFixed(1) : null;
  }, []);

  // Load portal configuration
  useEffect(() => {
    loadConfig();
  }, []);

  // Update document title when config is loaded
  useEffect(() => {
    if (config?.portalTitle) {
      document.title = config.portalTitle;
    }
  }, [config?.portalTitle]);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/public/config');
      const data = await response.json();

      if (!data.portalEnabled) {
        setError('The public portal is currently unavailable.');
        setLoading(false);
        return;
      }

      setConfig(data);
      setLoading(false);
    } catch (err) {
      setError('Unable to load portal configuration.');
      setLoading(false);
    }
  };

  // Load documents if enabled
  useEffect(() => {
    if (config?.showDocuments) {
      loadDocuments();
    }
  }, [config?.showDocuments]);

  const loadDocuments = async () => {
    try {
      const response = await fetch('/api/public/documents');
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  // Load products if enabled
  useEffect(() => {
    if (config?.showPricing) {
      loadProducts();
    }
  }, [config?.showPricing]);

  const loadProducts = async () => {
    try {
      const response = await fetch('/api/public/pricing');
      if (response.ok) {
        const data = await response.json();
        // Handle both old format (array) and new format (object with products)
        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          setProducts(data.products || []);
          setPricingShowTags(data.showTags || false);
          setPricingShowFilters(data.showFilters || false);
          setAvailableTags(data.availableTags || []);
        }
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  // Load availability if enabled
  useEffect(() => {
    if (config?.visibility?.plotAvailability) {
      loadAvailability();
    }
  }, [config?.visibility?.plotAvailability]);

  const loadAvailability = async () => {
    try {
      const response = await fetch('/api/public/availability');
      if (response.ok) {
        const data = await response.json();
        setAvailability(data);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!config || !mapRef.current || map) return;

    const mapboxMap = new mapboxgl.Map({
      container: 'public-map',
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: [-83.1174813, 33.8791535],
      zoom: 17,
      cooperativeGestures: true
    });

    mapboxMap.dragRotate.disable();
    mapboxMap.touchZoomRotate.disableRotation();

    mapboxMap.on('load', () => {
      mapboxMap.addControl(new mapboxgl.NavigationControl(), 'top-left');
      setMap(mapboxMap);

      if (mapboxMap.isStyleLoaded()) {
        setMapReady(true);
      } else {
        mapboxMap.once('idle', () => setMapReady(true));
      }

      // Load map data
      loadMapData(mapboxMap);
    });

    return () => {
      if (mapboxMap) mapboxMap.remove();
    };
  }, [config]);

  const loadMapData = async (mapInstance) => {
    try {
      const response = await fetch('/api/public/map');
      if (!response.ok) return;

      const geojson = await response.json();

      if (mapInstance.getSource('plots')) {
        mapInstance.getSource('plots').setData(geojson);
        return;
      }

      mapInstance.addSource('plots', {
        type: 'geojson',
        data: geojson
      });

      const colors = config?.mapColors || {
        available: '#22c55e',
        reserved: '#f59e0b',
        sold: '#3b82f6',
        interred: '#6b7280',
        hasVeteran: '#7c3aed'
      };

      // Add fill layer with veteran color support
      mapInstance.addLayer({
        id: 'plots-fill',
        type: 'fill',
        source: 'plots',
        paint: {
          'fill-color': [
            'case',
            // Veteran occupied plots (statusId = 3 and hasVeteran = true)
            ['all', ['==', ['get', 'statusId'], 3], ['==', ['get', 'hasVeteran'], true]],
            colors.hasVeteran || '#7c3aed',
            // Regular occupied plots (statusId = 3)
            ['==', ['get', 'statusId'], 3],
            colors.interred,
            // Sold plots (statusId = 2)
            ['==', ['get', 'statusId'], 2],
            colors.sold,
            // Reserved plots (statusId = 1)
            ['==', ['get', 'statusId'], 1],
            colors.reserved,
            // Available plots (statusId = 0 or default)
            colors.available
          ],
          'fill-opacity': 0.6
        }
      });

      // Add outline layer
      mapInstance.addLayer({
        id: 'plots-outline',
        type: 'line',
        source: 'plots',
        paint: {
          'line-color': '#ffffff',
          'line-width': 2
        }
      });

      // Add labels if alias is visible
      if (config?.visibility?.plotAlias) {
        mapInstance.addLayer({
          id: 'plots-labels',
          type: 'symbol',
          source: 'plots',
          layout: {
            'text-field': ['get', 'alias'],
            'text-size': 11,
            'text-anchor': 'center'
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 1
          }
        });
      }

      // Handle plot clicks
      mapInstance.on('click', 'plots-fill', async (e) => {
        const feature = e.features[0];
        if (!feature) return;

        const plotId = feature.properties.fid;
        setSelectedPlot(plotId);
        await loadPlotDetails(plotId, e.lngLat, mapInstance);
      });

      // Change cursor on hover
      mapInstance.on('mouseenter', 'plots-fill', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });
      mapInstance.on('mouseleave', 'plots-fill', () => {
        mapInstance.getCanvas().style.cursor = '';
      });

      // Fit bounds to plots
      if (geojson.features && geojson.features.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        geojson.features.forEach(feature => {
          if (feature.geometry && feature.geometry.coordinates) {
            const coords = feature.geometry.coordinates;
            if (feature.geometry.type === 'MultiPolygon') {
              coords.forEach(polygon => {
                polygon.forEach(ring => {
                  ring.forEach(coord => bounds.extend(coord));
                });
              });
            } else if (feature.geometry.type === 'Polygon') {
              coords.forEach(ring => {
                ring.forEach(coord => bounds.extend(coord));
              });
            }
          }
        });
        if (!bounds.isEmpty()) {
          mapInstance.fitBounds(bounds, { padding: 50 });
        }
      }
    } catch (err) {
      console.error('Failed to load map data:', err);
    }
  };

  const loadPlotDetails = async (plotId, lngLat, mapInstance = null, skipSearchParamsUpdate = false) => {
    // Use passed mapInstance or fall back to state
    const mapToUse = mapInstance || map;
    if (!mapToUse) {
      console.error('Map not available');
      return;
    }

    setLoadingDetails(true);
    try {
      const response = await fetch(`/api/public/plots/${plotId}`);
      if (!response.ok) throw new Error('Failed to load plot details');

      const details = await response.json();
      setPlotDetails(details);
      setCurrentImageIndex(0);

      // Remove existing popup
      if (popupRef.current) {
        popupRef.current.remove();
      }

      // Skip popup if no coordinates (deep-link case)
      if (!lngLat) {
        setShowDetailModal(true);
        if (!skipSearchParamsUpdate) {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('plot', plotId);
            return next;
          });
        }
        return;
      }

      // Create compact popup with "View Details" button
      let popupContent = `<div class="p-3 max-w-xs">`;

      if (config?.visibility?.plotAlias && details.alias) {
        popupContent += `<h3 class="text-lg font-semibold text-gray-900">${details.alias}</h3>`;
      }

      if (config?.visibility?.plotSection && details.section) {
        popupContent += `<p class="text-sm text-gray-600">Section: ${details.section}</p>`;
      }

      // Plot status
      if (config?.visibility?.plotStatus && details.statusId !== undefined) {
        const colors = config?.mapColors || {};
        let statusLabel = 'Available';
        let statusColor = colors.available || '#22c55e';

        if (details.statusId === 1) {
          statusLabel = 'Reserved';
          statusColor = colors.reserved || '#f59e0b';
        } else if (details.statusId === 2) {
          statusLabel = 'Sold';
          statusColor = colors.sold || '#3b82f6';
        } else if (details.statusId === 3) {
          if (details.hasVeteran && config?.visibility?.interredVeteranStatus) {
            statusLabel = 'Veteran Occupied';
            statusColor = colors.hasVeteran || '#7c3aed';
          } else {
            statusLabel = 'Occupied';
            statusColor = colors.interred || '#6b7280';
          }
        }

        popupContent += `<p class="text-sm mt-1"><span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white" style="background-color: ${statusColor}">${statusLabel}</span></p>`;
      }

      // Show first interred name if available
      if (details.interred && details.interred.length > 0) {
        popupContent += `<p class="text-sm text-gray-600 mt-1">${details.interred[0].name}`;
        if (details.interred.length > 1) {
          popupContent += ` <span class="text-gray-400">+${details.interred.length - 1} more</span>`;
        }
        popupContent += `</p>`;
      }

      // Add "View Details" button if there are attachments, flex fields, or more info
      const hasExtendedInfo = (details.attachments && details.attachments.length > 0) ||
        (details.flexFields && details.flexFields.length > 0) ||
        (details.interred && details.interred.length > 0) ||
        (details.owners && details.owners.length > 0);

      if (hasExtendedInfo) {
        popupContent += `<button onclick="document.dispatchEvent(new CustomEvent('showPlotDetail'))" class="mt-2 w-full text-center px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700">View Details</button>`;
      }

      popupContent += `</div>`;

      // Create and show popup
      const popup = new mapboxgl.Popup({ offset: 15, closeOnClick: true, maxWidth: '300px' })
        .setLngLat(lngLat)
        .setHTML(popupContent)
        .addTo(mapToUse);

      popupRef.current = popup;
    } catch (err) {
      console.error('Failed to load plot details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Listen for popup "View Details" button click
  useEffect(() => {
    const handleShowDetail = () => {
      setShowDetailModal(true);
      if (selectedPlot) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.set('plot', selectedPlot);
          return next;
        });
      }
    };
    document.addEventListener('showPlotDetail', handleShowDetail);
    return () => document.removeEventListener('showPlotDetail', handleShowDetail);
  }, [selectedPlot]);

  // Deep-link: load plot details from URL param on mount
  useEffect(() => {
    const plotParam = searchParams.get('plot');
    if (plotParam && config && !plotDetails && !loadingDetails) {
      loadPlotDetails(plotParam, null, null);
      setShowDetailModal(true);
    }
  }, [config, searchParams]);

  // Deep-link: load obituary from URL param on mount
  useEffect(() => {
    const obituaryParam = searchParams.get('obituary');
    if (obituaryParam && config) {
      setSelectedObituarySlug(obituaryParam);
      setActiveTab('obituaries');
    }
  }, [config, searchParams]);

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setPlotDetails(null);
    setCurrentImageIndex(0);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('plot');
      return next;
    });
  };

  // Image slider touch handlers
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
    touchEndRef.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    const diff = touchStartRef.current - touchEndRef.current;
    const images = plotDetails?.attachments?.filter(a => a.attachmentType === 'image') || [];
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentImageIndex < images.length - 1) {
        setCurrentImageIndex(prev => prev + 1);
      } else if (diff < 0 && currentImageIndex > 0) {
        setCurrentImageIndex(prev => prev - 1);
      }
    }
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  // Get status label helper
  const getStatusInfo = (statusId, hasVeteran) => {
    const colors = config?.mapColors || {};
    if (statusId === 1) return { label: 'Reserved', color: colors.reserved || '#f59e0b' };
    if (statusId === 2) return { label: 'Sold', color: colors.sold || '#3b82f6' };
    if (statusId === 3) {
      if (hasVeteran && config?.visibility?.interredVeteranStatus) {
        return { label: 'Veteran Occupied', color: colors.hasVeteran || '#7c3aed' };
      }
      return { label: 'Occupied', color: colors.interred || '#6b7280' };
    }
    return { label: 'Available', color: colors.available || '#22c55e' };
  };

  // Perform the actual search
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setSearching(true);
    setShowResults(true);

    try {
      const response = await fetch(`/api/public/search?q=${encodeURIComponent(query)}&limit=50`);
      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced auto-search as user types
  useEffect(() => {
    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Only trigger auto-search if query is at least 2 characters
    if (searchQuery.length >= 2) {
      searchDebounceRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300); // 300ms debounce
    } else {
      setSearchResults([]);
      setShowResults(false);
    }

    // Cleanup on unmount or when searchQuery changes
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  // Handle clicks outside search container to hide results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle focus on search input - show results if we have any
  const handleSearchFocus = () => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  // Search handler for form submit (immediate search)
  const handleSearch = async (e) => {
    e?.preventDefault();

    if (!searchQuery || searchQuery.length < 2) return;

    // Cancel any pending debounced search
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    performSearch(searchQuery);
  };

  // Handle result click - zoom to plot
  const handleResultClick = (result) => {
    if (!map || !result.centroid) return;

    // Scroll to the map container so user can see it
    if (mapRef.current) {
      mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const coords = result.centroid.coordinates;
    map.flyTo({ center: coords, zoom: 19, duration: 1000 });

    // Highlight the plot and show popup
    const plotId = result.fid || result.plotId;
    if (plotId) {
      setSelectedPlot(plotId);

      // Add highlight effect
      if (map.getLayer('plot-highlight')) map.removeLayer('plot-highlight');
      if (map.getSource('plot-highlight')) map.removeSource('plot-highlight');

      const highlightColor = config?.mapColors?.highlight || '#ef4444';

      // Find the plot feature and highlight it, then show popup
      setTimeout(() => {
        map.addSource('plot-highlight', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: result.centroid,
            properties: {}
          }
        });

        map.addLayer({
          id: 'plot-highlight',
          type: 'circle',
          source: 'plot-highlight',
          paint: {
            'circle-radius': 20,
            'circle-color': highlightColor,
            'circle-opacity': 0.6,
            'circle-stroke-width': 3,
            'circle-stroke-color': highlightColor
          }
        });

        // Load and show plot details popup
        loadPlotDetails(plotId, coords, map);

        // Remove highlight after a few seconds
        setTimeout(() => {
          if (map.getLayer('plot-highlight')) map.removeLayer('plot-highlight');
          if (map.getSource('plot-highlight')) map.removeSource('plot-highlight');
        }, 3000);
      }, 500);
    }

    setShowResults(false);
  };

  // Zoom to section bounds
  const zoomToSection = (sectionName) => {
    if (!map) return;

    const source = map.getSource('plots');
    if (!source || !source._data) return;

    const data = source._data;
    if (!data.features) return;

    // Filter features by section
    const sectionFeatures = data.features.filter(f =>
      f.properties?.section === sectionName
    );

    if (sectionFeatures.length === 0) return;

    // Calculate bounds
    const bounds = new mapboxgl.LngLatBounds();

    sectionFeatures.forEach(feature => {
      if (!feature.geometry?.coordinates) return;

      const coords = feature.geometry.coordinates;
      if (feature.geometry.type === 'MultiPolygon') {
        coords.forEach(polygon => {
          polygon.forEach(ring => {
            ring.forEach(coord => bounds.extend(coord));
          });
        });
      } else if (feature.geometry.type === 'Polygon') {
        coords.forEach(ring => {
          ring.forEach(coord => bounds.extend(coord));
        });
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 50, duration: 1000 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <InformationCircleIcon className="h-16 w-16 text-gray-400 mx-auto" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Portal Unavailable</h1>
          <p className="mt-2 text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              {config?.siteLogo ? (
                <img
                  src={config.siteLogo}
                  alt={config?.portalTitle || 'Cemetery Portal'}
                  className="h-10 w-auto max-w-[120px] object-contain"
                />
              ) : (
                <MapPinIcon className="h-8 w-8 text-indigo-600" />
              )}
              <h1 className="ml-2 text-xl font-bold text-gray-900">
                {config?.portalTitle || 'Cemetery Portal'}
              </h1>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>

            {/* Desktop navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => setActiveTab('map')}
                className={`flex items-center text-sm font-medium ${
                  activeTab === 'map' ? 'text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MapIcon className="h-5 w-5 mr-1" />
                Map
              </button>

              {config?.showDocuments && documents.length > 0 && (
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`flex items-center text-sm font-medium ${
                    activeTab === 'documents' ? 'text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <DocumentTextIcon className="h-5 w-5 mr-1" />
                  Documents
                </button>
              )}

              {config?.showPricing && products.length > 0 && (
                <button
                  onClick={() => setActiveTab('pricing')}
                  className={`flex items-center text-sm font-medium ${
                    activeTab === 'pricing' ? 'text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <CurrencyDollarIcon className="h-5 w-5 mr-1" />
                  Pricing
                </button>
              )}

              {config?.showContactInfo && config?.contactInfo && (
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`flex items-center text-sm font-medium ${
                    activeTab === 'contact' ? 'text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <PhoneIcon className="h-5 w-5 mr-1" />
                  Contact
                </button>
              )}

              {config?.showObituaries && (
                <button
                  onClick={() => setActiveTab('obituaries')}
                  className={`flex items-center text-sm font-medium ${
                    activeTab === 'obituaries' ? 'text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <HeartIcon className="h-5 w-5 mr-1" />
                  {config?.obituaryPageTitle || 'In Memoriam'}
                </button>
              )}

              {config?.externalLinks?.map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  {link.title}
                </a>
              ))}

              {showLoginLink && (
                <Link
                  to="/login"
                  className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  <UserIcon className="h-5 w-5 mr-1" />
                  Login
                </Link>
              )}

              {showAccountLink && (
                <Link
                  to="/settings"
                  className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  <UserIcon className="h-5 w-5 mr-1" />
                  Account
                </Link>
              )}
            </nav>
          </div>

          {/* Mobile navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t">
              <div className="space-y-2">
                <button
                  onClick={() => { setActiveTab('map'); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center px-3 py-2 rounded-md ${
                    activeTab === 'map' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600'
                  }`}
                >
                  <MapIcon className="h-5 w-5 mr-2" />
                  Map
                </button>

                {config?.showDocuments && documents.length > 0 && (
                  <button
                    onClick={() => { setActiveTab('documents'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center px-3 py-2 rounded-md ${
                      activeTab === 'documents' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600'
                    }`}
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    Documents
                  </button>
                )}

                {config?.showPricing && products.length > 0 && (
                  <button
                    onClick={() => { setActiveTab('pricing'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center px-3 py-2 rounded-md ${
                      activeTab === 'pricing' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600'
                    }`}
                  >
                    <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                    Pricing
                  </button>
                )}

                {config?.showContactInfo && config?.contactInfo && (
                  <button
                    onClick={() => { setActiveTab('contact'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center px-3 py-2 rounded-md ${
                      activeTab === 'contact' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600'
                    }`}
                  >
                    <PhoneIcon className="h-5 w-5 mr-2" />
                    Contact
                  </button>
                )}

                {config?.showObituaries && (
                  <button
                    onClick={() => { setActiveTab('obituaries'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center px-3 py-2 rounded-md ${
                      activeTab === 'obituaries' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600'
                    }`}
                  >
                    <HeartIcon className="h-5 w-5 mr-2" />
                    {config?.obituaryPageTitle || 'In Memoriam'}
                  </button>
                )}

                {showLoginLink && (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full flex items-center px-3 py-2 rounded-md text-gray-600"
                  >
                    <UserIcon className="h-5 w-5 mr-2" />
                    Login
                  </Link>
                )}

                {showAccountLink && (
                  <Link
                    to="/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full flex items-center px-3 py-2 rounded-md text-gray-600"
                  >
                    <UserIcon className="h-5 w-5 mr-2" />
                    Account
                  </Link>
                )}
              </div>
            </nav>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Welcome Message */}
        {config?.welcomeMessage && activeTab === 'map' && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
            <p className="text-gray-700">{config.welcomeMessage}</p>
          </div>
        )}

        {/* Map Tab - Always render but hide when not active to preserve map state */}
        <div className={`space-y-4 ${activeTab !== 'map' ? 'hidden' : ''}`}>
            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <form onSubmit={handleSearch} className="relative" ref={searchContainerRef}>
                <div className="flex">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={handleSearchFocus}
                      placeholder="Search by name, plot number, or section..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={searching || !searchQuery || searchQuery.length < 2}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-r-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {searching ? 'Searching...' : 'Search'}
                  </button>
                </div>

                {/* Search Results Dropdown */}
                {showResults && searchResults.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                    <div className="p-2 text-xs text-gray-500 border-b">
                      {searchResults.length} result{searchResults.length !== 1 && 's'} found
                    </div>
                    {searchResults.map((result, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleResultClick(result)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-gray-900">
                            {result.type === 'interred' ? result.name : result.alias || `Plot ${result.fid}`}
                          </div>
                          <div className="text-sm text-gray-500">
                            {result.type === 'interred' && result.plotAlias && `Plot: ${result.plotAlias}`}
                            {result.section && ` - Section: ${result.section}`}
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}

                {showResults && searchResults.length === 0 && !searching && searchQuery.length >= 2 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                    No results found for "{searchQuery}"
                  </div>
                )}
              </form>

              {/* Clear results button - show when there are results (visible or not) */}
              {searchResults.length > 0 && (
                <button
                  onClick={() => { setShowResults(false); setSearchResults([]); setSearchQuery(''); }}
                  className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear results
                </button>
              )}
            </div>

            {/* Map Legend */}
            {config?.mapColors && (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Map Legend</h3>
                <div className="flex flex-wrap gap-4">
                  {/* Only show Available, Reserved, Sold when plotAvailability is enabled */}
                  {config.visibility?.plotAvailability && (
                    <>
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded mr-2"
                          style={{ backgroundColor: config.mapColors.available }}
                        ></div>
                        <span className="text-sm text-gray-600">Available</span>
                      </div>
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded mr-2"
                          style={{ backgroundColor: config.mapColors.reserved }}
                        ></div>
                        <span className="text-sm text-gray-600">Reserved</span>
                      </div>
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded mr-2"
                          style={{ backgroundColor: config.mapColors.sold }}
                        ></div>
                        <span className="text-sm text-gray-600">Sold</span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 rounded mr-2"
                      style={{ backgroundColor: config.mapColors.interred }}
                    ></div>
                    <span className="text-sm text-gray-600">Occupied</span>
                  </div>
                  {config.mapColors.hasVeteran && config.visibility?.interredVeteranStatus && (
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded mr-2"
                        style={{ backgroundColor: config.mapColors.hasVeteran }}
                      ></div>
                      <span className="text-sm text-gray-600">Veteran Occupied</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Availability Stats */}
            {config?.visibility?.plotAvailability && availability?.bySection && (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Availability by Section (click to zoom)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {availability.bySection.map((section, idx) => (
                    <button
                      key={idx}
                      onClick={() => section.section && zoomToSection(section.section)}
                      disabled={!section.section}
                      className={`bg-gray-50 rounded-lg p-3 text-center transition-colors ${
                        section.section
                          ? 'hover:bg-indigo-50 hover:ring-2 hover:ring-indigo-200 cursor-pointer'
                          : 'cursor-default'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">{section.section || 'Unassigned'}</div>
                      <div className="text-lg font-bold text-green-600">{section.available}</div>
                      <div className="text-xs text-gray-500">of {section.total} available</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile Map Gesture Help */}
            <div className="md:hidden bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p className="font-medium mb-1">Map Controls:</p>
              <ul className="text-xs space-y-0.5">
                <li>• <strong>Pinch</strong> to zoom in/out</li>
                <li>• <strong>Two fingers</strong> to pan the map</li>
                <li>• <strong>One finger</strong> scrolls the page</li>
                <li>• <strong>Tap</strong> a plot to view details</li>
              </ul>
            </div>

            {/* Map Container */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div
                id="public-map"
                ref={mapRef}
                style={{ width: '100%', height: '500px', touchAction: 'pan-y' }}
              ></div>
            </div>

            {/* Plot Detail Modal */}
            {showDetailModal && plotDetails && (
              <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50" onClick={closeDetailModal}>
                <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b bg-gray-50 z-10 rounded-t-lg">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {config?.visibility?.plotAlias ? (plotDetails.alias || `Plot ${plotDetails.fid}`) : 'Plot Details'}
                    </h3>
                    {config?.visibility?.plotSection && plotDetails.section && (
                      <p className="text-sm text-gray-500">Section: {plotDetails.section}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {config?.visibility?.plotStatus && plotDetails.statusId !== undefined && (
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium text-white"
                        style={{ backgroundColor: getStatusInfo(plotDetails.statusId, plotDetails.hasVeteran).color }}
                      >
                        {getStatusInfo(plotDetails.statusId, plotDetails.hasVeteran).label}
                      </span>
                    )}
                    <button
                      onClick={closeDetailModal}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="p-4 space-y-6">
                  {/* Image Slider */}
                  {config?.visibility?.plotAttachments && plotDetails.attachments && (() => {
                    const images = plotDetails.attachments.filter(a => a.attachmentType === 'image');
                    if (images.length === 0) return null;

                    return (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700 flex items-center">
                          <PhotoIcon className="h-4 w-4 mr-1.5" />
                          Photos ({images.length})
                        </h4>
                        {/* Slider Container */}
                        <div className="relative rounded-lg overflow-hidden bg-gray-900">
                          <div
                            className="relative w-full"
                            style={{ paddingBottom: '60%' }}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                          >
                            <img
                              src={images[currentImageIndex]?.fileData}
                              alt={images[currentImageIndex]?.fileName || 'Plot photo'}
                              className="absolute inset-0 w-full h-full object-contain cursor-pointer"
                              onClick={() => setSliderFullscreen(true)}
                            />
                          </div>

                          {/* Navigation Arrows */}
                          {images.length > 1 && (
                            <>
                              <button
                                onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
                                disabled={currentImageIndex === 0}
                                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ChevronLeftIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => setCurrentImageIndex(prev => Math.min(images.length - 1, prev + 1))}
                                disabled={currentImageIndex === images.length - 1}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ChevronRightIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}

                          {/* Dot Indicators */}
                          {images.length > 1 && (
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                              {images.map((_, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setCurrentImageIndex(idx)}
                                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                                    idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                                  }`}
                                />
                              ))}
                            </div>
                          )}

                          {/* Image Counter */}
                          {images.length > 1 && (
                            <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 text-white text-xs rounded">
                              {currentImageIndex + 1} / {images.length}
                            </div>
                          )}
                        </div>

                        {/* Caption */}
                        {images[currentImageIndex]?.fileName && (
                          <p className="text-xs text-gray-500 text-center">{images[currentImageIndex].fileName}</p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Documents & Links */}
                  {config?.visibility?.plotAttachments && plotDetails.attachments && (() => {
                    const docs = plotDetails.attachments.filter(a => a.attachmentType === 'document');
                    const links = plotDetails.attachments.filter(a => a.attachmentType === 'link');
                    if (docs.length === 0 && links.length === 0) return null;

                    return (
                      <div className="space-y-2">
                        {docs.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 flex items-center mb-2">
                              <PaperClipIcon className="h-4 w-4 mr-1.5" />
                              Documents
                            </h4>
                            <div className="space-y-1">
                              {docs.map((doc, idx) => (
                                <a
                                  key={idx}
                                  href={doc.fileData}
                                  download={doc.fileName}
                                  className="flex items-center px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm text-gray-700"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4 mr-2 text-gray-400" />
                                  {doc.fileName || 'Download document'}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {links.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 flex items-center mb-2">
                              <LinkIcon className="h-4 w-4 mr-1.5" />
                              Links
                            </h4>
                            <div className="space-y-1">
                              {links.map((link, idx) => (
                                <a
                                  key={idx}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm text-indigo-600"
                                >
                                  <LinkIcon className="h-4 w-4 mr-2" />
                                  {link.fileName || link.url}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Flex Fields */}
                  {config?.visibility?.plotFlexFields && plotDetails.flexFields && plotDetails.flexFields.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Information</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {plotDetails.flexFields.map((field, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg px-3 py-2">
                            <span className="text-xs text-gray-500">{field.label}</span>
                            <p className="text-sm text-gray-900 mt-0.5">
                              {field.type === 'checkbox' ? (field.value === 'true' ? 'Yes' : 'No') : (field.value || '-')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interred Information */}
                  {plotDetails.interred && plotDetails.interred.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Interred</h4>
                      <div className="space-y-2">
                        {plotDetails.interred.map((person, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg px-3 py-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-900">{person.name}</span>
                                {person.intermentDate && (
                                  <span className="text-sm text-gray-500 ml-2">
                                    ({new Date(person.intermentDate).toLocaleDateString()})
                                  </span>
                                )}
                              </div>
                              {config?.showObituaryOnPlot && person.obituarySlug && (
                                <button
                                  onClick={() => {
                                    setSelectedObituarySlug(person.obituarySlug);
                                    setSearchParams(prev => {
                                      const next = new URLSearchParams(prev);
                                      next.set('obituary', person.obituarySlug);
                                      return next;
                                    });
                                  }}
                                  className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
                                >
                                  <HeartIcon className="h-4 w-4 mr-1" />
                                  View Obituary
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Owner Information */}
                  {plotDetails.owners && plotDetails.owners.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Owners</h4>
                      <div className="space-y-2">
                        {plotDetails.owners.map((owner, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg px-3 py-2">
                            <span className="font-medium text-gray-900">{owner.name}</span>
                            {(owner.phone || owner.email || owner.address) && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {owner.phone && <div>{owner.phone}</div>}
                                {owner.email && <div>{owner.email}</div>}
                                {owner.address && <div>{owner.address}</div>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Plot Size */}
                  {config?.visibility?.plotSize && plotDetails.geometry && (() => {
                    const plotArea = calculatePlotArea(plotDetails.geometry);
                    if (!plotArea) return null;
                    return (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Estimated Size:</span> ~{plotArea} sq ft
                      </div>
                    );
                  })()}
                </div>
              </div>
              </div>
            )}

            {/* Fullscreen Image Viewer */}
            {sliderFullscreen && plotDetails?.attachments && (() => {
              const images = plotDetails.attachments.filter(a => a.attachmentType === 'image');
              if (images.length === 0) return null;

              return (
                <div
                  className="fixed inset-0 z-50 bg-black flex items-center justify-center"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {/* Close button */}
                  <button
                    onClick={() => setSliderFullscreen(false)}
                    className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 z-10"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>

                  {/* Image */}
                  <img
                    src={images[currentImageIndex]?.fileData}
                    alt={images[currentImageIndex]?.fileName || 'Plot photo'}
                    className="max-w-full max-h-full object-contain"
                  />

                  {/* Navigation */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
                        disabled={currentImageIndex === 0}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30"
                      >
                        <ChevronLeftIcon className="h-6 w-6" />
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex(prev => Math.min(images.length - 1, prev + 1))}
                        disabled={currentImageIndex === images.length - 1}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30"
                      >
                        <ChevronRightIcon className="h-6 w-6" />
                      </button>
                    </>
                  )}

                  {/* Counter */}
                  {images.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/50 text-white text-sm rounded-full">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  )}
                </div>
              );
            })()}
        </div>

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Documents</h2>

            {documents.length === 0 ? (
              <p className="text-gray-500">No documents available.</p>
            ) : (
              <div className="grid gap-4">
                {['rules', 'pricing', 'forms', 'photos', 'other'].map(category => {
                  const categoryDocs = documents.filter(d => d.category === category);
                  if (categoryDocs.length === 0) return null;

                  const categoryLabels = {
                    rules: 'Rules & Regulations',
                    pricing: 'Pricing Information',
                    forms: 'Forms & Applications',
                    photos: 'Photos',
                    other: 'Other Documents'
                  };

                  return (
                    <div key={category}>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">{categoryLabels[category]}</h3>
                      <ul className="divide-y divide-gray-200 border rounded-lg">
                        {categoryDocs.map(doc => (
                          <li key={doc.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                            <div>
                              <h4 className="font-medium text-gray-900">{doc.title}</h4>
                              {doc.description && (
                                <p className="text-sm text-gray-500">{doc.description}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {doc.fileName} ({(doc.fileSize / 1024).toFixed(1)} KB)
                              </p>
                            </div>
                            <a
                              href={`/api/public/documents/${doc.id}/download`}
                              className="flex items-center px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100"
                            >
                              <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                              Download
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Products & Services</h2>

            {/* Tag Filters */}
            {pricingShowFilters && availableTags.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedTagFilter(null)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedTagFilter === null
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {availableTags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => setSelectedTagFilter(tag.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        selectedTagFilter === tag.id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {products.length === 0 ? (
              <p className="text-gray-500">No pricing information available.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {products
                  .filter(product => {
                    if (!selectedTagFilter) return true;
                    return product.tags?.some(tag => tag.id === selectedTagFilter);
                  })
                  .map(product => (
                  <div key={product.id} className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900">{product.name}</h3>
                    {product.description && (
                      <p className="text-sm text-gray-500 mt-1">{product.description}</p>
                    )}
                    <p className="mt-2 text-xl font-bold text-green-600">
                      ${Number(product.price).toFixed(2)}
                    </p>
                    {pricingShowTags && product.tags && product.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {product.tags.map(tag => (
                          <span
                            key={tag.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700"
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Show message if filtering results in no products */}
            {selectedTagFilter && products.filter(p => p.tags?.some(t => t.id === selectedTagFilter)).length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No products found for the selected filter.
              </p>
            )}

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                For inquiries or to purchase, please contact us.
              </p>
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && config?.contactInfo && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>

            <div className="space-y-4">
              {config.portalTitle && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{config.portalTitle}</h3>
                </div>
              )}

              {config.contactInfo.address && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Address</h4>
                  <p className="text-gray-600 whitespace-pre-line">{config.contactInfo.address}</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {config.contactInfo.phone && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Phone</h4>
                    <a href={`tel:${config.contactInfo.phone}`} className="text-indigo-600 hover:text-indigo-500">
                      {config.contactInfo.phone}
                    </a>
                  </div>
                )}

                {config.contactInfo.email && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Email</h4>
                    <a href={`mailto:${config.contactInfo.email}`} className="text-indigo-600 hover:text-indigo-500">
                      {config.contactInfo.email}
                    </a>
                  </div>
                )}
              </div>

              {config.contactInfo.hours && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Office Hours</h4>
                  <p className="text-gray-600">{config.contactInfo.hours}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Obituaries Tab */}
        {activeTab === 'obituaries' && config?.showObituaries && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <PublicObituaries
              config={config}
              onSelectObituary={(slug) => {
                setSelectedObituarySlug(slug);
                setSearchParams(prev => {
                  const next = new URLSearchParams(prev);
                  next.set('obituary', slug);
                  return next;
                });
              }}
            />
          </div>
        )}

        {/* Obituary Detail Modal */}
        {selectedObituarySlug && (
          <PublicObituaryDetail
            slug={selectedObituarySlug}
            config={config}
            onClose={() => {
              setSelectedObituarySlug(null);
              setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('obituary');
                return next;
              });
            }}
            onViewPlot={(plotId) => {
              setSelectedObituarySlug(null);
              setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('obituary');
                next.set('plot', plotId);
                return next;
              });
              setActiveTab('map');
              loadPlotDetails(plotId, null, null, true);
              setShowDetailModal(true);
            }}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} {config?.portalTitle || 'Cemetery Portal'}</p>

            {config?.privacyPolicy && (
              <button
                onClick={() => alert(config.privacyPolicy)}
                className="mt-2 md:mt-0 text-gray-500 hover:text-gray-700"
              >
                Privacy Policy
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicPortal;
